import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { 
  CreditCard, ArrowUpRight, ArrowDownRight, Search, DollarSign, 
  TrendingUp, Download, RefreshCw, AlertTriangle, Filter, Calendar,
  Plus, X, Trash2
} from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';

interface FinanceOverview {
  totalMRR: number;
  totalOrganizations: number;
  totalUsers: number;
  totalInvoices: number;
  totalRevenue: number;
  totalExpense: number;
  netBalance: number;
  mrrGrowth: number;
  churn: number;
  activeSubscribers: number;
}

interface Transaction {
  id: string;
  description: string;
  amount: number;
  type: 'RECEITA' | 'DESPESA';
  date: string;
  status: string;
  organizationName: string;
  paymentMethod: string;
}

export default function FinanceAdminView() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const [formDesc, setFormDesc] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formType, setFormType] = useState<'RECEITA' | 'DESPESA'>('RECEITA');
  const [formDate, setFormDate] = useState('');

  const { data: overview, isLoading: loadingOverview, refetch: refetchOverview } = useQuery<FinanceOverview>({
    queryKey: ['admin-finance-overview'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/finance/overview');
      const payload = res.data?.data ?? res.data;
      return payload;
    },
  });

  const { data: transactions = [], isLoading: loadingTx, refetch: refetchTx } = useQuery<Transaction[]>({
    queryKey: ['admin-finance-transactions', { search: searchTerm, type: typeFilter }],
    queryFn: async () => {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (typeFilter !== 'ALL') params.type = typeFilter;
      const res = await apiClient.get('/api/admin/finance/transactions', { params });
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/api/admin/finance/transactions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-finance-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-finance-overview'] });
      showSuccess('Transação criada com sucesso!');
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao criar transação.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiClient.put(`/api/admin/finance/transactions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-finance-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-finance-overview'] });
      showSuccess('Transação atualizada com sucesso!');
      setIsModalOpen(false);
      setEditingTx(null);
      resetForm();
    },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao atualizar transação.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/admin/finance/transactions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-finance-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-finance-overview'] });
      showSuccess('Transação excluída com sucesso!');
    },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao excluir transação.'),
  });

  const resetForm = () => {
    setFormDesc('');
    setFormAmount('');
    setFormType('RECEITA');
    setFormDate('');
    setEditingTx(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (tx: Transaction) => {
    setEditingTx(tx);
    setFormDesc(tx.description);
    setFormAmount(String(tx.amount));
    setFormType(tx.type);
    setFormDate(new Date(tx.date).toISOString().slice(0, 16));
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDesc.trim() || !formAmount) return;

    const payload = {
      description: formDesc,
      amount: parseFloat(formAmount),
      type: formType === 'RECEITA' ? 'CREDIT' : 'DEBIT',
      occurredAt: formDate || new Date().toISOString(),
    };

    if (editingTx) {
      const rawId = editingTx.id.replace(/^(TX|INV)-/, '');
      updateMutation.mutate({ id: rawId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const totalRevenue = overview?.totalRevenue ?? 0;
  const totalExpense = overview?.totalExpense ?? 0;
  const netBalance = overview?.netBalance ?? (totalRevenue - totalExpense);
  const totalMRR = overview?.totalMRR ?? 0;

  const handleExportCSV = () => {
    const headers = 'ID,Descricao,Valor,Tipo,Data,Status,Organizacao,Metodo\n';
    const rows = transactions
      .map(
        (t) =>
          `"${t.id}","${t.description}",${t.amount},"${t.type}","${t.date}","${t.status}","${t.organizationName}","${t.paymentMethod}"`,
      )
      .join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio_financeiro_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 tracking-wider uppercase mb-1">
            <DollarSign size={14} /> Transações & Fluxo de Caixa Global
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Financeiro & Transações
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Monitoramento em tempo real de receitas de assinaturas SaaS, faturamento e custos operacionais no PostgreSQL.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              refetchOverview();
              refetchTx();
            }}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium shadow-xs transition-all"
          >
            <RefreshCw size={13} className={loadingOverview || loadingTx ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 rounded-xl text-xs font-semibold shadow-xs transition-all"
          >
            <Download size={14} /> Exportar CSV
          </button>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-xs transition-all"
          >
            <Plus size={14} /> Nova Transação
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">MRR Estimado</p>
            <h3 className="text-2xl font-bold text-indigo-600 mt-1">
              R$ {totalMRR.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </h3>
            <span className="text-[11px] text-indigo-600 font-medium flex items-center gap-1 mt-1">
              <TrendingUp size={12} /> {overview?.activeSubscribers || 0} assinantes ativos
            </span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <CreditCard size={20} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Receita Total</p>
            <h3 className="text-2xl font-bold text-emerald-600 mt-1">
              R$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </h3>
            <span className="text-[11px] text-emerald-600 font-medium flex items-center gap-1 mt-1">
              <TrendingUp size={12} /> Faturas Pagas
            </span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <ArrowUpRight size={20} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Despesas Registradas</p>
            <h3 className="text-2xl font-bold text-rose-600 mt-1">
              R$ {totalExpense.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </h3>
            <span className="text-[11px] text-gray-500 font-medium mt-1 block">Custos de infraestrutura</span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <ArrowDownRight size={20} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Saldo Líquido</p>
            <h3 className={`text-2xl font-bold mt-1 ${netBalance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              R$ {netBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </h3>
            <span className="text-[11px] text-gray-500 font-medium mt-1 block">Margem Operacional</span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-gray-50 text-gray-700 flex items-center justify-center">
            <DollarSign size={20} />
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por descrição ou organização..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500 transition-all"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <Filter size={14} className="text-gray-400" />
          <span className="text-xs font-semibold text-gray-600">Tipo:</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-hidden"
          >
            <option value="ALL">Todas as Transações</option>
            <option value="RECEITA">Apenas Receitas</option>
            <option value="DESPESA">Apenas Despesas</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {loadingTx ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs text-gray-500 mt-3 font-medium">Carregando transações do banco...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-semibold text-gray-700">Nenhuma transação encontrada</p>
            <p className="text-xs text-gray-400 mt-1">Nenhum registro financeiro corresponde aos filtros aplicados.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50/75 border-b border-gray-200/80 text-gray-500 font-semibold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">Código / ID</th>
                  <th className="py-3 px-4">Descrição</th>
                  <th className="py-3 px-4">Organização</th>
                  <th className="py-3 px-4">Método</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Data</th>
                  <th className="py-3 px-4 text-right">Valor</th>
                  <th className="py-3 px-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3.5 px-4 font-mono text-[11px] text-gray-500 font-medium">
                      {tx.id}
                    </td>
                    <td className="py-3.5 px-4 font-medium text-gray-900">
                      {tx.description}
                    </td>
                    <td className="py-3.5 px-4 text-gray-600 font-medium">
                      {tx.organizationName}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-[10px] font-semibold">
                        {tx.paymentMethod}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        tx.status === 'Pago' || tx.status === 'Concluído'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tx.status === 'Pago' || tx.status === 'Concluído' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        {tx.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                      {new Date(tx.date).toLocaleDateString('pt-BR')}
                    </td>
                    <td className={`py-3.5 px-4 text-right font-bold text-xs ${
                      tx.type === 'RECEITA' ? 'text-emerald-600' : 'text-rose-600'
                    }`}>
                      {tx.type === 'RECEITA' ? '+' : '-'} R$ {Number(tx.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(tx)}
                          className="p-1.5 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition"
                          title="Editar"
                        >
                          <RefreshCw size={14} />
                        </button>
                        <button
                          onClick={() => {
                            const rawId = tx.id.replace(/^(TX|INV)-/, '');
                            if (confirm('Tem certeza que deseja excluir esta transação?')) {
                              deleteMutation.mutate(rawId);
                            }
                          }}
                          className="p-1.5 hover:text-red-600 hover:bg-rose-50 rounded-md transition"
                          title="Excluir"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl border border-gray-200 max-w-lg w-full overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div className="text-left">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                  {editingTx ? <RefreshCw size={16} className="text-indigo-600" /> : <Plus size={16} className="text-indigo-600" />}
                  {editingTx ? 'Editar Transação' : 'Nova Transação'}
                </h3>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {editingTx ? 'Atualize os dados da movimentação financeira.' : 'Registre uma nova receita ou despesa manualmente.'}
                </p>
              </div>
              <button onClick={() => { setIsModalOpen(false); resetForm(); }} className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-400 hover:text-gray-900 transition-colors">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 text-left">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Descrição *</label>
                <input
                  type="text"
                  required
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  placeholder="Ex: Pagamento AWS / Consultoria Tech"
                  className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-indigo-500 rounded-xl px-3 py-2.5 text-xs font-medium outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Valor (R$) *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formAmount}
                    onChange={(e) => setFormAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-indigo-500 rounded-xl px-3 py-2.5 text-xs font-medium outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tipo *</label>
                  <select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value as 'RECEITA' | 'DESPESA')}
                    className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-indigo-500 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none"
                  >
                    <option value="RECEITA">Receita (+)</option>
                    <option value="DESPESA">Despesa (-)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Data</label>
                <input
                  type="datetime-local"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="w-full bg-[#FAFAFA] border border-gray-200 focus:border-indigo-500 rounded-xl px-3 py-2.5 text-xs font-medium outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setIsModalOpen(false); resetForm(); }}
                  className="px-4 py-2 text-xs font-semibold text-gray-550 hover:bg-gray-100 rounded-xl transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold transition disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Salvando...' : (editingTx ? 'Atualizar' : 'Criar Transação')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
